import { Component } from 'react'
import PropTypes from 'prop-types'
import { applyMiddleware, combineReducers, createStore } from 'redux'

// @ts-ignore
import thunk from 'redux-thunk'
import { Provider } from 'react-redux'
import { withStaticProps } from 'lib/decorators'
import { withContextualizedTracking } from 'lib/analytics'

import { settingsPath } from 'lib/urls'

import withCurrentUser from 'shared/HOC/withCurrentUser'
import withReactQueryClientProvider from 'shared/HOC/withReactQueryClientProvider'

import { SET_REGISTRY, SET_USER, SET_USER_CREDIT } from 'registry/actions'
import registryReducer from 'registry/reducers'

import RegistrySettingsPage from './RegistrySettingsPage'

import {
  AccountInfoSection,
  RegistryInfoSection,
  BirthdayPreferencesSection,
  RegistryCollaboratorsSection,
  ShippingAddressSection,
  ConsolidatedShippingSection,
  CashFundPreferencesSection,
  RegistryVisibilitySection,
  EmailPreferencesSection,
  GiftCardsAndPromotionsSection,
  YourOrdersSection,
  NavigationSettings,
  ShipmentViewSection,
} from './constants'

import { CurrentUser, Responses } from '../../global'
import Router from 'shared/universal-router'
import { Routes, Route } from 'react-router-dom-v5-compat'

let middlewares = [thunk]

const createStoreWithMiddleware = applyMiddleware(...middlewares)(createStore)

const initReduxDevTools = () => {
  if (
    typeof window === 'object' &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
  ) {
    return window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__()
  }
}

interface SettingsAppProps {
  context: {
    countries: string[]
    states: { [key: string]: string[] }
    devMode: boolean
  }
  registry: Responses.Registry
  currentUser: CurrentUser
  userCredit: any
  webView: boolean
  path: string
}

interface SettingsAppStore {
  dispatch: (action: any) => void
}

class SettingsApp extends Component<SettingsAppProps> {
  static propTypes = {
    registry: PropTypes.object.isRequired,
    currentUser: PropTypes.object,
    userCredit: PropTypes.object.isRequired,
    webView: PropTypes.bool,
  }

  static childContextTypes = {
    countries: PropTypes.arrayOf(PropTypes.string),
    states: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    devMode: PropTypes.bool,
  }

  getChildContext() {
    return this.props.context
  }

  store: SettingsAppStore

  constructor(props: SettingsAppProps) {
    super(props)

    this.store = createStoreWithMiddleware(
      combineReducers(registryReducer),
      initReduxDevTools()
    )

    // bootstrap reducers with data injected as a prop from `react_component()`
    this.store.dispatch({
      type: SET_REGISTRY,
      registry: props.registry,
    })

    this.store.dispatch({
      type: SET_USER_CREDIT,
      userCredit: props.userCredit,
    })
  }

  componentDidUpdate(prevProps: Readonly<SettingsAppProps>): void {
    const { currentUser } = this.props
    if (!prevProps.currentUser && currentUser) {
      this.store.dispatch({
        type: SET_USER,
        user: currentUser,
      })
    }
  }

  render() {
    const { webView, path } = this.props

    // TODO: Refactor to not need withStaticProps
    const DecoratedYourOrders = withStaticProps('YourOrders', {
      webView,
    })(YourOrdersSection.component)

    const navigation = [
      AccountInfoSection,
      RegistryInfoSection,
      BirthdayPreferencesSection,
      RegistryCollaboratorsSection,
      ShippingAddressSection,
      ConsolidatedShippingSection,
      CashFundPreferencesSection,
      RegistryVisibilitySection,
      EmailPreferencesSection,
      GiftCardsAndPromotionsSection,
      ShipmentViewSection,
      {
        ...YourOrdersSection,
        component: DecoratedYourOrders,
      },
    ].filter((route) => route) as Array<NavigationSettings>

    const routeDefinition = navigation.map((route) => (
      <Route
        path={route.route}
        element={<route.component />}
        key={route.path}
      />
    ))

    const IndexComponent = navigation[0].component

    return (
      <Provider store={this.store}>
        {/* @ts-ignore */}
        <Router
          location={{ pathname: path }}
          initialEntries={[{ pathname: path }]}
          useV6Router
        >
          <Routes>
            <Route
              path={settingsPath}
              element={
                <RegistrySettingsPage
                  navigation={navigation}
                  webView={webView}
                />
              }
            >
              <Route index element={<IndexComponent />} />
              {routeDefinition}
            </Route>
          </Routes>
        </Router>
      </Provider>
    )
  }
}

export default withCurrentUser(
  withReactQueryClientProvider(withContextualizedTracking()(SettingsApp))
)
